import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { match } from 'ts-pattern';

import {
  useGetBitsoBanksByCountryCodeQuery,
  useGetTransactionDetailsQuery,
  useGetTransactionStatusLogsQuery,
  useLazyDownloadTransactionDetailsQuery,
} from '@/api/v1';
import { DetailsStatus } from '@/components/molecules/DetailsStatus';
import { DetailsValue } from '@/components/molecules/DetailsValue';
import { DownloadDocumentButton } from '@/components/molecules/DownloadDocumentButton';
import { FullScreenLoader } from '@/components/molecules/FullScreenLoader';
import { TransactionDetailsDrawerContent } from '@/components/molecules/TransactionDetailsDrawerContent';
import { TransactionDetailsDrawerStatus } from '@/components/molecules/TransactionDetailsDrawerStatus';
import { TransactionDocumentsList } from '@/components/molecules/TransactionDocumentsList';
import { Accordion } from '@/components/organisms/Accordion';
import { usePaymentIdentifiersFields } from '@/hooks/usePaymentIdentifiersFields';
import { useTranslator } from '@/hooks/useTranslator';
import { Amount } from '@/types/amount';
import {
  ACHAccountDetails,
  COPAccountDetails,
  CVUAccountDetails,
  isFiatAccount,
  PIXAccountDetails,
  SpeiAccountDetails,
  SwiftAccountDetails,
  WireAccountDetails,
} from '@/types/contacts';
import {
  isACHAccountDetails,
  isCOPAccountDetails,
  isCVUAccountDetails,
  isPIXAccountDetails,
  isSpeiAccountDetails,
  isSwiftAccountDetails,
  isWireAccountDetails,
} from '@/types/contacts.ext';
import { CountryType } from '@/types/country';
import { CurrencyCodeType, PaymentRailType } from '@/types/currency';
import { DetailsField } from '@/types/detailsField';
import { ProofOfPaymentType } from '@/types/proofOfPaymentType';
import { isRecipientResponse } from '@/types/recipient';
import { isAwaitingFunding, simplifyTransactionStatus, TransactionType } from '@/types/transaction';

enum DrawerSection {
  STATUS = 'STATUS',
  DETAILS = 'DETAILS',
  PAYMENT_IDENTIFIERS = 'PAYMENT_IDENTIFIERS',
  DOWNLOAD_TRANSACTION_DOCUMENTS = 'DOWNLOAD_TRANSACTION_DOCUMENTS',
}

interface TransactionDrawerAlert {
  readonly message: string;
  readonly action: string;
}

export const TransactionDetailsDrawer: React.FC = (): React.ReactElement => {
  const { transactionId = '' } = useParams<{ readonly transactionId: string | undefined }>();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { data: transactionDetails, isLoading: isLoadingDetails } =
    useGetTransactionDetailsQuery(transactionId);
  const {
    data: { statusLogs, nextStatus } = { statusLogs: [], nextStatus: [] },
    isLoading: isLoadingStatusLogs,
  } = useGetTransactionStatusLogsQuery(transactionId);

  const [fetchProofOfPayment, fetchProofOfPaymentResult] = useLazyDownloadTransactionDetailsQuery();
  const [expandedSections, setExpandedSections] = React.useState<ReadonlySet<DrawerSection>>(
    new Set(),
  );
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  const accordionRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  const fromCountry = React.useMemo((): string => {
    return getAccountCountry(transactionDetails?.fromAccount);
  }, [transactionDetails?.fromAccount]);

  const toCountry = React.useMemo((): string => {
    return getAccountCountry(transactionDetails?.toRecipient ?? transactionDetails?.toAccount);
  }, [transactionDetails?.toRecipient, transactionDetails?.toAccount]);

  const { data: fromBanks = [] } = useGetBitsoBanksByCountryCodeQuery(fromCountry, {
    skip: fromCountry === '',
  });

  const { data: toBanks = [] } = useGetBitsoBanksByCountryCodeQuery(toCountry, {
    skip: toCountry === '',
  });

  const tr = useTranslator();
  const paymentIdentifiersFields = usePaymentIdentifiersFields(transactionDetails);
  const hasPaymentIdentifiers = React.useMemo(
    (): boolean =>
      paymentIdentifiersFields.some((field: DetailsField | null): boolean => field !== null),
    [paymentIdentifiersFields],
  );
  const drawerAlerts = React.useMemo((): readonly TransactionDrawerAlert[] => {
    if (!transactionDetails) {
      return [];
    }
    const list: TransactionDrawerAlert[] = [];
    if (transactionDetails.efx && !transactionDetails.hasAcam220File) {
      list.push({
        message: tr('PENDING_ACAM_220_FILE_ALERT'),
        action: tr('PENDING_ACAM_220_FILE_ALERT_TRANSACTION_DETAILS_DRAWER_ACTION'),
      });
    }
    if (isAwaitingFunding(transactionDetails)) {
      list.push({
        message: tr('AWAITING_FUNDING_ALERT'),
        action: tr('AWAITING_FUNDING_ALERT_TRANSACTION_DETAILS_DRAWER_ACTION'),
      });
    }
    return list;
  }, [transactionDetails, tr]);

  const onDownloadDocument = React.useCallback(
    (type: ProofOfPaymentType): void => {
      fetchProofOfPayment({
        transactionId: transactionId,
        type: type,
      });
    },
    [fetchProofOfPayment, transactionId],
  );

  const onLinkedTransactionClick = React.useCallback(
    (linkedId: string): void => {
      navigate(
        {
          pathname: `../${linkedId}`,
          search: search.toString(),
        },
        { relative: 'path' },
      );
    },
    [navigate, search],
  );

  const toName =
    transactionDetails?.toRecipient?.individualName ??
    transactionDetails?.toRecipient?.businessName ??
    transactionDetails?.toAccount?.name ??
    '';

  const depositFromName =
    transactionDetails?.payInSender?.details?.senderName ??
    transactionDetails?.fromAccount?.name ??
    '';

  const title = match<TransactionType | undefined, string>(transactionDetails?.type)
    .with(TransactionType.DEPOSIT, (): string => tr('DEPOSIT_FROM', tr(depositFromName as string)))
    .with(TransactionType.EXCHANGE, (): string => tr('EXCHANGE'))
    .with(TransactionType.WITHDRAWAL, (): string => tr('WITHDRAWAL_TO', tr(toName)))
    .with(TransactionType.TRANSFER, (): string => tr('TRANSFER_TO', tr(toName)))
    .with(TransactionType.REFUND, (): string =>
      tr('REFUND_TO', transactionDetails?.beneficiaryName ?? ''),
    )
    .otherwise((): string => tr('TRANSACTION'));

  React.useEffect((): void => {
    if (
      fetchProofOfPaymentResult.status === QueryStatus.fulfilled ||
      fetchProofOfPaymentResult.status === QueryStatus.rejected
    ) {
      fetchProofOfPaymentResult.reset();
    }
  }, [fetchProofOfPaymentResult]);

  const onAccordionExpanded = React.useCallback((section: DrawerSection): VoidFunction => {
    return (): void => {
      setExpandedSections(
        (currentSections: ReadonlySet<DrawerSection>): ReadonlySet<DrawerSection> => {
          const newSections = new Set(currentSections);
          if (newSections.has(section)) {
            newSections.delete(section);
          } else {
            newSections.add(section);
            // Scroll into view after a short delay to allow the accordion to expand
            setTimeout((): void => {
              const accordionEl = accordionRefs.current[section];
              if (accordionEl) {
                accordionEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 150);
          }
          return newSections;
        },
      );
    };
  }, []);

  React.useEffect((): void => {
    if (isLoadingStatusLogs || isInitialized) {
      return;
    }

    const initialSections = new Set<DrawerSection>();
    if (statusLogs.length > 0) {
      initialSections.add(DrawerSection.STATUS);
    }
    initialSections.add(DrawerSection.DETAILS);
    setExpandedSections(initialSections);
    setIsInitialized(true);
  }, [isLoadingStatusLogs, statusLogs.length, isInitialized]);

  const isReady = isInitialized;

  const isBRLTransaction = React.useMemo((): boolean => {
    const { fromAmount, toAmount } = transactionDetails ?? {
      fromAmount: Amount.zero(CurrencyCodeType.UNSET),
      toAmount: Amount.zero(CurrencyCodeType.UNSET),
    };

    return (
      fromAmount.currencyCode === CurrencyCodeType.BRL ||
      toAmount.currencyCode === CurrencyCodeType.BRL
    );
  }, [transactionDetails]);

  const availableDocuments = React.useMemo((): readonly ProofOfPaymentType[] => {
    return Array.from(
      new Set([
        ProofOfPaymentType.DETAILED_PAYMENT_CONFIRMATION,
        ProofOfPaymentType.PAYMENT_CONFIRMATION,
        ...(isBRLTransaction ? [ProofOfPaymentType.COMPROVANTE_OPERACAO] : []),
      ]),
    );
  }, [isBRLTransaction]);

  if (!isReady || isLoadingDetails || isLoadingStatusLogs) {
    return (
      <Box width={612} height="100%" padding={6}>
        <FullScreenLoader loading={true} />
      </Box>
    );
  } else if (!transactionDetails) {
    return (
      <Box width={612} height="100%" padding={6}>
        <Typography variant="body1" color="error">
          {tr('TRANSACTION_NOT_FOUND')}
        </Typography>
      </Box>
    );
  } else {
    return (
      <Box width={612} height="100%" padding={6}>
        <Box sx={styles.header}>
          <Typography variant="h6">{title}</Typography>
          <DetailsStatus
            status={simplifyTransactionStatus(
              transactionDetails.status,
              transactionDetails.completed,
            )}
          />
        </Box>
        {drawerAlerts.map(
          (alert: TransactionDrawerAlert, index: number): React.ReactElement => (
            <Alert key={index} severity="warning" sx={styles.acam220Alert}>
              <Typography variant="body1" color="text.secondary">
                {alert.message}{' '}
                <Typography component="strong" fontWeight={600}>
                  {alert.action}
                </Typography>
              </Typography>
            </Alert>
          ),
        )}
        {statusLogs.length > 0 && (
          <div
            ref={(el: HTMLDivElement | null): void => {
              accordionRefs.current[DrawerSection.STATUS] = el;
            }}>
            <Accordion
              baseSx={styles.accordionBase}
              detailsSx={styles.accordionDetails}
              summarySx={styles.accordionSummary}
              expanded={expandedSections.has(DrawerSection.STATUS)}
              onChange={onAccordionExpanded(DrawerSection.STATUS)}
              label={tr('STATUS')}>
              <TransactionDetailsDrawerStatus statusLogs={statusLogs} nextStatus={nextStatus} />
            </Accordion>
          </div>
        )}
        <div
          ref={(el: HTMLDivElement | null): void => {
            accordionRefs.current[DrawerSection.DETAILS] = el;
          }}>
          <Accordion
            baseSx={styles.accordionBase}
            detailsSx={styles.accordionDetails}
            summarySx={styles.accordionSummary}
            expanded={expandedSections.has(DrawerSection.DETAILS)}
            onChange={onAccordionExpanded(DrawerSection.DETAILS)}
            label={tr('DETAILS')}>
            <TransactionDetailsDrawerContent
              transaction={transactionDetails}
              fromBanks={fromBanks}
              toBanks={toBanks}
              onLinkedTransactionClick={onLinkedTransactionClick}
            />
            <TransactionDocumentsList
              transactionId={transactionDetails.id}
              documents={transactionDetails.documents}
              isEfx={transactionDetails.efx}
            />
          </Accordion>
        </div>
        {hasPaymentIdentifiers && (
          <div
            ref={(el: HTMLDivElement | null): void => {
              accordionRefs.current[DrawerSection.PAYMENT_IDENTIFIERS] = el;
            }}>
            <Accordion
              baseSx={styles.accordionBase}
              detailsSx={styles.accordionDetails}
              summarySx={styles.accordionSummary}
              expanded={expandedSections.has(DrawerSection.PAYMENT_IDENTIFIERS)}
              onChange={onAccordionExpanded(DrawerSection.PAYMENT_IDENTIFIERS)}
              label={tr('PAYMENT_IDENTIFIERS')}>
              <Box my={4}>
                <Grid container={true} spacing={4}>
                  {paymentIdentifiersFields
                    .filter((item: DetailsField | null): item is DetailsField => item !== null)
                    .map((item: DetailsField): React.ReactElement => {
                      const columnSpan = Math.max(item.columnSpan ?? 1, 1);
                      return (
                        <React.Fragment key={item.label}>
                          <Grid component={Box} size={columnSpan * 6}>
                            <Typography variant="body1" color="custom.LightGray">
                              {item.label}
                            </Typography>
                            <DetailsValue title={item.title}>{item.value}</DetailsValue>
                          </Grid>
                        </React.Fragment>
                      );
                    })}
                </Grid>
              </Box>
            </Accordion>
          </div>
        )}
        <div
          ref={(el: HTMLDivElement | null): void => {
            accordionRefs.current[DrawerSection.DOWNLOAD_TRANSACTION_DOCUMENTS] = el;
          }}>
          <Accordion
            baseSx={styles.accordionBase}
            detailsSx={styles.accordionDetails}
            summarySx={styles.accordionSummary}
            expanded={expandedSections.has(DrawerSection.DOWNLOAD_TRANSACTION_DOCUMENTS)}
            onChange={onAccordionExpanded(DrawerSection.DOWNLOAD_TRANSACTION_DOCUMENTS)}
            label={tr('DOWNLOAD_TRANSACTION_DOCUMENTS')}>
            <Box mt={2}>
              {availableDocuments.map((type: ProofOfPaymentType): React.ReactElement => {
                const { type: statusType } = fetchProofOfPaymentResult.originalArgs ?? {
                  type: ProofOfPaymentType.NONE,
                };

                return (
                  <DownloadDocumentButton
                    key={type}
                    status={
                      statusType === type
                        ? fetchProofOfPaymentResult.status
                        : QueryStatus.uninitialized
                    }
                    disabled={fetchProofOfPaymentResult.status === QueryStatus.pending}
                    type={type}
                    onClick={onDownloadDocument}
                  />
                );
              })}
            </Box>
          </Accordion>
        </div>
      </Box>
    );
  }
};

const recipientTypeToCountry: Partial<Record<PaymentRailType, CountryType>> = {
  [PaymentRailType.ACH]: CountryType.US,
  [PaymentRailType.Wire]: CountryType.US,
  [PaymentRailType.PIX]: CountryType.BR,
  [PaymentRailType.COP]: CountryType.CO,
  [PaymentRailType.SPEI]: CountryType.MX,
  [PaymentRailType.CVU]: CountryType.AR,
};

const getAccountCountry = (account: unknown): string => {
  // Handle RecipientResponse
  if (isRecipientResponse(account)) {
    if (account.type === PaymentRailType.Swift) {
      const bankAddr = account.details?.bankAddress as Record<string, unknown> | undefined;
      return (bankAddr?.country as string) ?? '';
    }
    return recipientTypeToCountry[account.type] ?? '';
  }

  if (!isFiatAccount(account)) {
    return '';
  }

  return match(account.accountDetails)
    .when(isACHAccountDetails, (_: ACHAccountDetails): string => 'US')
    .when(isWireAccountDetails, (_: WireAccountDetails): string => 'US')
    .when(
      isSwiftAccountDetails,
      (details: SwiftAccountDetails): string => details.swiftDetails.bankInfo.country,
    )
    .when(isPIXAccountDetails, (_: PIXAccountDetails): string => 'BR')
    .when(isCOPAccountDetails, (_: COPAccountDetails): string => 'CO')
    .when(isSpeiAccountDetails, (_: SpeiAccountDetails): string => 'MX')
    .when(isCVUAccountDetails, (_: CVUAccountDetails): string => 'AR')
    .otherwise((): string => '');
};

const styles: Record<string, SystemStyleObject> = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    pb: 4,
    gap: 3,
    borderBottomWidth: 0,
    borderBottomStyle: 'solid',
    borderBottomColor: 'custom.DividerMed',
  },
  accordionBase: {
    boxShadow: 'none',
    backgroundColor: 'custom.OffWhite',
    borderTop: '1px solid',
    borderTopColor: 'custom.DividerMed',
    '&:before': {
      display: 'none',
    },
  },
  accordionSummary: {
    boxShadow: 'none',
    backgroundColor: 'custom.OffWhite',
    px: 0,
  },
  accordionDetails: {
    px: 0,
    marginTop: '-24px',
    boxShadow: 'none',
    backgroundColor: 'custom.OffWhite',
  },
  icon: {
    px: 0,
    ':hover': {
      backgroundColor: 'transparent',
    },
    ':disabled': {
      backgroundColor: 'transparent',
      color: 'custom.LightGray',
    },
  },
  acam220Alert: {
    mb: 4,
  },
};
